import React from 'react';
import { Link } from 'react-router-dom';
import { Check, ArrowRight, FileText } from 'lucide-react';

const APP_URL = '/login';

const FILINGS_DATA = {
  'llc': {
    title: 'LLC Filings & Amendments',
    items: [
      { name: 'Articles of Organization', slug: 'articles-of-organization' },
      { name: 'Operating Agreement', slug: 'operating-agreement' },
      { name: 'Annual Report / Renewal', slug: 'annual-report' },
      { name: 'Registered Agent Appointment', slug: 'registered-agent-appointment' },
      { name: 'Name Amendment', slug: 'name-amendment' },
      { name: 'Member/Manager Change', slug: 'member-manager-change' },
      { name: 'Dissolution Filing', slug: 'dissolution-filing' }
    ]
  },
  'c-corp': {
    title: 'C-Corp Filings & Amendments',
    items: [
      { name: 'Articles of Incorporation', slug: 'articles-of-incorporation' },
      { name: 'Corporate Bylaws', slug: 'corporate-bylaws' },
      { name: 'Annual Report', slug: 'annual-report' },
      { name: 'Registered Agent Appointment', slug: 'registered-agent-appointment' },
      { name: 'Stock Amendment', slug: 'stock-amendment' },
      { name: 'Director/Officer Change', slug: 'director-officer-change' },
      { name: 'Dissolution Filing', slug: 'dissolution-filing' }
    ]
  },
  'partnership': {
    title: 'Partnership Filings & Amendments',
    items: [
      { name: 'Certificate of Partnership', slug: null },
      { name: 'Partnership Agreement', slug: null },
      { name: 'Annual Report', slug: 'annual-report' },
      { name: 'Registered Agent Appointment', slug: 'registered-agent-appointment' },
      { name: 'Partner Change Filing', slug: null },
      { name: 'Amendment Filing', slug: 'name-amendment' },
      { name: 'Dissolution Filing', slug: 'dissolution-filing' }
    ]
  },
  'general': {
    title: 'Business Filings & Amendments',
    items: [
      { name: 'Formation Documents', slug: 'articles-of-organization' },
      { name: 'Annual Report / Renewal', slug: 'annual-report' },
      { name: 'Registered Agent Service', slug: 'registered-agent-appointment' },
      { name: 'Name Amendment', slug: 'name-amendment' },
      { name: 'Ownership/Structure Change', slug: 'member-manager-change' },
      { name: 'Foreign Qualification', slug: null },
      { name: 'Dissolution Filing', slug: 'dissolution-filing' }
    ]
  }
};

const FilingsSection = ({ entityType = 'general', stateName = null }) => {
  const filings = FILINGS_DATA[entityType] || FILINGS_DATA['general'];
  
  return (
    <section className="section-padding bg-[#111c17]" data-testid="filings-section">
      <div className="container-custom">
        <div className="text-center mb-12">
          <span className="text-emerald-600 font-semibold text-sm uppercase tracking-widest">Ongoing Support</span>
          <h2 className="text-3xl md:text-4xl text-white mt-3 mb-4">
            {filings.title}
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            We handle all state filings and amendments throughout the lifecycle of your business.
            {stateName && ` Available for ${stateName} entities.`}
          </p>
        </div>
        
        <div className="max-w-3xl mx-auto">
          <div className="bg-[#070d0b] border border-white/10 p-8">
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              {filings.items.map((item, index) => (
                <div key={index} className="flex items-center gap-3" data-testid={`filing-item-${index}`}>
                  <div className="w-8 h-8 bg-emerald-50 flex items-center justify-center flex-shrink-0">
                    <FileText className="w-4 h-4 text-emerald-600" />
                  </div>
                  {item.slug ? (
                    <Link 
                      to={`/usa/filings/${item.slug}/`}
                      className="text-white hover:text-emerald-600 transition-colors font-medium"
                    >
                      {item.name}
                    </Link>
                  ) : (
                    <span className="text-white font-medium">{item.name}</span>
                  )}
                </div>
              ))}
            </div>
            
            <div className="bg-[#111c17] p-6 flex flex-col sm:flex-row items-center justify-between gap-4 border border-white/10">
              <div>
                <p className="text-slate-400 text-sm">Filing & Amendment Fee</p>
                <p className="text-2xl font-bold text-white">
                  $200 <span className="text-slate-400 text-base font-normal">+ state fee</span>
                </p>
              </div>
              <a href={APP_URL} className="inline-flex items-center justify-center px-6 py-3 bg-[#1e293b] text-white font-medium hover:bg-slate-800 transition-colors" data-testid="filings-section-cta">
                Request Filing
                <ArrowRight className="w-4 h-4 ml-2" />
              </a>
            </div>
          </div>
          
          <div className="text-center mt-8">
            <Link to="/usa/filings/articles-of-organization/" className="text-emerald-600 hover:text-emerald-700 font-medium inline-flex items-center gap-2 transition-colors">
              Learn about each filing type
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FilingsSection;
